import {
  UploadableEntity,
  useUploadFileMutation,
} from '@shared/generated/graphql';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

enum ChatUploadStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  UPLOADING = 'UPLOADING',
}

type ChatUpload = {
  file: File;
  status: ChatUploadStatus;
  uploadId?: string;
  uploadUrl?: string;
  uuid: string;
};

type ChatUploadState = {
  file?: File;
  hasError?: boolean;
  id: string;
  isUploading: boolean;
  uploadId?: string;
  url?: string;
};

type UseChatUploadFilesValue = {
  remove: (id: string) => void;
  resetUploader: () => void;
  startUploads: (files: File[]) => void;
  uploadStates: ChatUploadState[];
};

type UseChatUploadFilesOptions = {
  organizationId: string;
  subjectId?: string;
  subjectType: UploadableEntity;
};

export function useChatUploadFiles(
  options: UseChatUploadFilesOptions
): UseChatUploadFilesValue {
  const [uploadFile] = useUploadFileMutation();
  const [uploads, setUploads] = useState<ChatUpload[]>([]);
  const [uploadIds, setUploadIds] = useState<string[]>([]);
  const [excludeIds, setExcludeIds] = useState<string[]>([]);

  const updateUpload = (
    uuid: string,
    updates: Pick<ChatUpload, 'status' | 'uploadId' | 'uploadUrl'>
  ) => {
    setUploads((currentUploads) =>
      currentUploads.map((upload) =>
        upload.uuid === uuid ? { ...upload, ...updates } : upload
      )
    );
  };

  const startUpload = async (upload: ChatUpload): Promise<void> => {
    updateUpload(upload.uuid, { status: ChatUploadStatus.UPLOADING });
    try {
      const { data, errors } = await uploadFile({
        variables: {
          file: upload.file,
          input: {
            description: upload.file.name,
            organizationId: options.organizationId,
            subjectId: options.subjectId,
            subjectType: options.subjectType,
          },
        },
      });
      if (errors && errors.length > 0) {
        console.error(errors);
      }
      if (errors?.length || !data?.uploadFile.success) {
        updateUpload(upload.uuid, { status: ChatUploadStatus.ERROR });
        return;
      }
      updateUpload(upload.uuid, {
        status: ChatUploadStatus.SUCCESS,
        uploadId: data.uploadFile.upload?.id,
        uploadUrl: data.uploadFile.url,
      });
    } catch (error) {
      console.error(error);
      updateUpload(upload.uuid, { status: ChatUploadStatus.ERROR });
    }
  };

  const addFileToUpload = (file: File): ChatUpload => {
    const upload = {
      file,
      status: ChatUploadStatus.PENDING,
      uuid: uuidv4(),
    };
    setUploads((currentUploads) => [...currentUploads, upload]);
    void startUpload(upload);
    return upload;
  };

  const startUploads = (files: File[]) => {
    const newUploadIds = files.map((file) => addFileToUpload(file).uuid);
    setUploadIds((currentIds) => [...currentIds, ...newUploadIds]);
  };

  const resetUploader = () => {
    setUploadIds([]);
    setExcludeIds([]);
    setUploads([]);
  };

  const uploadStates = uploadIds
    .map((id) => {
      const upload = uploads.find(({ uuid }) => uuid === id);
      return {
        id,
        uploadId: upload?.uploadId,
        isUploading:
          upload?.status === ChatUploadStatus.UPLOADING ||
          upload?.status === ChatUploadStatus.PENDING,
        url: upload?.uploadUrl,
        file: upload?.file,
        hasError: upload?.status === ChatUploadStatus.ERROR,
      };
    })
    .filter(({ id }) => !excludeIds.includes(id));

  return {
    remove: (id) => setExcludeIds((currentIds) => [...currentIds, id]),
    resetUploader,
    startUploads,
    uploadStates,
  };
}
